/**
 * Date utility functions for consistent timestamp handling across the application.
 *
 * The pg driver adds local timezone offset to timestamps without timezone,
 * so we need to compensate when displaying times.
 */

/**
 * Formats a database timestamp for display, correcting for pg driver timezone offset.
 * @param timestamp - ISO timestamp string from the database
 * @param options - Formatting options
 * @returns Formatted date/time string
 */
export function formatTimestamp(
  timestamp: string | null | undefined,
  options: {
    showDate?: boolean;
    showTime?: boolean;
    showSeconds?: boolean;
    dateFormat?: 'short' | 'long';
  } = {}
): string {
  if (!timestamp) return '-';

  const { showDate = true, showTime = true, showSeconds = true, dateFormat = 'short' } = options;

  // Database stores timestamps in UTC. JavaScript Date parses them correctly.
  // No timezone adjustment needed - just parse and display in local time.
  const date = new Date(timestamp);
  const adjustedDate = date;

  const parts: string[] = [];

  if (showDate) {
    if (dateFormat === 'short') {
      parts.push(
        adjustedDate.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })
      );
    } else {
      parts.push(
        adjustedDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      );
    }
  }

  if (showTime) {
    parts.push(
      adjustedDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        ...(showSeconds && { second: '2-digit' }),
      })
    );
  }

  return parts.join(' ');
}

/**
 * Formats a timestamp showing only the date (MM/DD/YYYY)
 */
export function formatDate(timestamp: string | null | undefined): string {
  return formatTimestamp(timestamp, { showDate: true, showTime: false });
}

/**
 * Formats a timestamp showing only the time (HH:MM:SS AM/PM)
 */
export function formatTime(timestamp: string | null | undefined): string {
  return formatTimestamp(timestamp, { showDate: false, showTime: true, showSeconds: true });
}

/**
 * Formats a timestamp showing date and time (MM/DD/YYYY HH:MM AM/PM)
 */
export function formatDateTime(timestamp: string | null | undefined): string {
  return formatTimestamp(timestamp, { showDate: true, showTime: true });
}

/**
 * Convert a date string (YYYY-MM-DD) or Date to Julian date code (YYDDD format).
 * YY = 100 - (year % 100), DDD = day of year (001-366).
 * Example: 2026-03-03 → "74062"
 *
 * If no argument is passed, uses today's date.
 */
export function getDateCode(dateStr?: string | Date): string {
  const date = dateStr
    ? typeof dateStr === 'string'
      ? new Date(dateStr.split('T')[0] + 'T00:00:00')
      : dateStr
    : new Date();
  const year = date.getFullYear();
  const yearCode = 100 - (year % 100);
  // Use Date.UTC to avoid DST spring-forward stealing 1 hour and rounding day down
  const utcDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const utcStart = Date.UTC(date.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((utcDate - utcStart) / (1000 * 60 * 60 * 24)) + 1;
  return `${yearCode}${dayOfYear.toString().padStart(3, '0')}`;
}

/**
 * Convert a Julian date code (YYDDD) back to a YYYY-MM-DD string.
 * YY = 100 - (year % 100), so 74 → 2026.
 * Handles both 4-digit (e.g. "7062") and 5-digit (e.g. "74062") codes.
 * Example: "74062" → "2026-03-03"
 */
export function fromDateCode(code: string): string {
  if (!code || code.length < 4 || code.length > 5) return '';
  const yyPart = code.length === 5 ? code.slice(0, 2) : code.slice(0, 1);
  const dddPart = code.length === 5 ? code.slice(2) : code.slice(1);
  const yy = 100 - parseInt(yyPart, 10);
  const doy = parseInt(dddPart, 10);
  if (isNaN(yy) || isNaN(doy) || doy < 1 || doy > 366) return '';
  const year = 2000 + yy;
  const d = new Date(year, 0, doy);
  if (isNaN(d.getTime())) return '';
  return toLocalISODate(d);
}

/**
 * Subtract 1 day from a YYYY-MM-DD date string. Returns YYYY-MM-DD.
 */
export function getDayBefore(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  date.setDate(date.getDate() - 1);
  return toLocalISODate(date);
}

/**
 * Get the previous business day (Mon–Fri) before a YYYY-MM-DD date string.
 * If the day before is a weekend, skip back to Friday.
 * Example: Monday → Friday, Tuesday → Monday, Sunday → Friday.
 */
export function getBusinessDayBefore(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  date.setDate(date.getDate() - 1);
  // Skip weekends: 0 = Sunday, 6 = Saturday
  const day = date.getDay();
  if (day === 0) date.setDate(date.getDate() - 2); // Sunday → Friday
  if (day === 6) date.setDate(date.getDate() - 1); // Saturday → Friday
  return toLocalISODate(date);
}

/**
 * Format a duration in hours as H:MM:SS.
 * Example: 1.5 → "1:30:00"
 */
export function formatElapsedTime(hours: number): string {
  const totalSeconds = Math.floor(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/**
 * Format a duration in decimal hours as "Xh Ym" from the real value; minutes floored.
 * Example: 7.7631 → "7h 45m", 0.5 → "30m".
 */
export function formatActiveHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.floor((hours - h) * 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Convert a Date object to YYYY-MM-DD string using local timezone.
 * Avoids UTC pitfalls from toISOString().
 */
export function toLocalISODate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Format a date string (YYYY-MM-DD or ISO) as "Mar 3, 2026" for display.
 * Returns '—' for null/undefined input.
 */
export function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const normalized = dateStr.length === 10 ? dateStr + 'T00:00:00' : dateStr;
  const d = new Date(normalized);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format a date string as MM/DD/YYYY. Returns '—' for null/undefined.
 * Handles both YYYY-MM-DD and full ISO timestamp strings.
 */
export function formatMMDDYYYY(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const normalized = dateStr.length === 10 ? dateStr + 'T00:00:00' : dateStr;
  const d = new Date(normalized);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Format a timestamp as "date time" for display (e.g. "3/4/2026 02:15 PM").
 * Uses browser locale for date, short time format.
 */
export function formatDateTimeLocal(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
